import React, { useState, useEffect, useCallback } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { toast } from 'react-toastify';
import { ClientRestaurant } from './types';
import { getLocalsByClient, addLocalToClient } from '../../../crud/clients_api/local_clients';
import PortalLayout from "./components/PortalLayout/PortalLayout";
import Home from "./components/Home/Home";
import Cupones from "./components/Cupones/Cupones";
import Profile from "./components/Profile/Profile";
import RestaurantDetails from "./components/RestaurantDetails/RestaurantDetails";
import LoadingSpinner from '../../../components/common/LoadingSpinner';

const ClientPortal: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [restaurants, setRestaurants] = useState<ClientRestaurant[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRestaurant, setSelectedRestaurant] = useState<ClientRestaurant | null>(null);
  const [showRestaurantDetails, setShowRestaurantDetails] = useState(false);

  // Check authentication
  const isAuthenticated = () => {
    const token = localStorage.getItem('access_token');
    const client = localStorage.getItem('client');
    return !!(token && client);
  };

  const getClientId = (): number | null => {
    const clientData = localStorage.getItem('client');
    if (clientData) {
      try {
        const client = JSON.parse(clientData);
        return client.client_id || null;
      } catch {
        return null;
      }
    }
    return null;
  };

  // Fetch restaurants for the client
  const fetchRestaurants = useCallback(async () => {
    const clientId = getClientId();
    if (!clientId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const response: any = await getLocalsByClient(clientId);
      setRestaurants(response.data || []);
    } catch (error: any) {
      console.error('Error fetching restaurants:', error);
      toast.error('Error al cargar tus restaurantes');
      setRestaurants([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Handle adding local to client
  const handleAddLocalToClient = useCallback(async (localId: number) => {
    const clientId = getClientId();
    if (!clientId) {
      console.error('No client ID found');
      return false;
    }

    try {
      const response: any = await addLocalToClient(clientId, localId);
      if (response.status === 201) {
        toast.success('¡Restaurante agregado exitosamente!');
        // Refresh the restaurants list
        await fetchRestaurants();
        return true;
      }
      return false;
    } catch (error: any) {
      console.error('Error adding local to client:', error);
      const message = error?.message || 'Error al agregar el restaurante';
      toast.error(message);
      return false;
    }
  }, [fetchRestaurants]);

  // Handle local_id from URL parameters
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const localId = searchParams.get('local_id');
    
    if (localId) {
      const localIdNumber = parseInt(localId, 10);
      
      if (isAuthenticated()) {
        // User is logged in, add local directly
        handleAddLocalToClient(localIdNumber).then((success) => {
          if (success) {
            // Remove local_id from URL after successful addition
            searchParams.delete('local_id');
            const newSearch = searchParams.toString();
            navigate(`${location.pathname}${newSearch ? `?${newSearch}` : ''}`, { replace: true });
          }
        });
      } else {
        // User not logged in, store for later and redirect to auth
        sessionStorage.setItem('pending_local_id', localId);
        navigate(`/ClientAuth/ClientPortal/Home${location.search}`);
      }
    }

    // Reset restaurant details when navigating away from Home
    if (!location.pathname.includes('/Home')) {
      setShowRestaurantDetails(false);
      setSelectedRestaurant(null);
    }
  }, [location.pathname, location.search, navigate, handleAddLocalToClient]);

  // Fetch restaurants on mount and when authenticated
  useEffect(() => {
    if (isAuthenticated()) {
      fetchRestaurants();
      
      // Check if there's a pending local_id from registration/login
      const pendingLocalId = sessionStorage.getItem('pending_local_id');
      if (pendingLocalId) {
        const localIdNumber = parseInt(pendingLocalId, 10);
        sessionStorage.removeItem('pending_local_id');
        
        handleAddLocalToClient(localIdNumber).then(() => {
          // Remove from URL if present
          const searchParams = new URLSearchParams(location.search);
          if (searchParams.has('local_id')) {
            searchParams.delete('local_id');
            const newSearch = searchParams.toString();
            navigate(`${location.pathname}${newSearch ? `?${newSearch}` : ''}`, { replace: true });
          }
        });
      }
    } else {
      setLoading(false);
    }
  }, [fetchRestaurants, handleAddLocalToClient, location.pathname, location.search, navigate]);

  const handleRestaurantClick = (restaurant: ClientRestaurant) => {
    setSelectedRestaurant(restaurant);
    setShowRestaurantDetails(true);
  };

  const handleBackToHome = () => {
    setShowRestaurantDetails(false);
    setSelectedRestaurant(null);
  };

  if (loading) {
    return <LoadingSpinner text="Cargando tus restaurantes..." />;
  }

  return (
    <Routes>
      <Route
        path="/"
        element={
          isAuthenticated() ? (
            <Navigate to="/ClientPortal/Home" replace />
          ) : (
            <Navigate to="/ClientAuth/ClientPortal/Home" replace />
          )
        }
      />
      <Route
        path="/Home"
        element={
          <PortalLayout>
            {showRestaurantDetails && selectedRestaurant ? (
              <RestaurantDetails 
                restaurant={selectedRestaurant} 
                onBack={handleBackToHome}
              />
            ) : (
              <Home 
                restaurants={restaurants}
                onRestaurantClick={handleRestaurantClick}
              />
            )}
          </PortalLayout>
        }
      />
      <Route
        path="/Cupones"
        element={
          <PortalLayout>
            <Cupones />
          </PortalLayout>
        }
      />
      <Route
        path="/Profile"
        element={
          <PortalLayout>
            <Profile />
          </PortalLayout>
        }
      />
      <Route path="*" element={<Navigate to="/ClientPortal/Home" replace />} />
    </Routes>
  );
};

export default ClientPortal;